import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, addDoc, collection, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { FaUserCheck, FaUserPlus } from 'react-icons/fa';
import { useFollow } from '../utils/FollowContext';
import defaultAvatar from '../assets/svg/images/default-avatar.jpg';
import VerificationBadge from '../components/VerificationBadge';

export function UserItem({ userId }) {
  const auth = getAuth();
  const navigate = useNavigate();
  const {
    isFollowing,
    shouldFollowBack,
    toggleFollow,
    isInitialized
  } = useFollow();
  const [userData, setUserData] = useState({
    profileImage: defaultAvatar,
    name: 'Unknown',
    username: 'unknown',
    followersCount: 0,
    verified: false
  });
  const [hasListings, setHasListings] = useState(false);
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(null);
  const currentUser = auth.currentUser;

  // Fetch user data and check for listings
  useEffect(() => {
    async function fetchUserData() {
      try {
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          setUserData({
            profileImage: data.photoURL || defaultAvatar,
            name: data.name || 'Unknown',
            username: data.username || 'unknown',
            followersCount: data.followers?.length || 0,
            verified: data.verified || false
          });

          // Check if user has any listings
          const listingsQuery = query(
            collection(db, 'listings'),
            where('userRef', '==', userId)
          );
          const listingsSnap = await getDocs(listingsQuery);
          setHasListings(!listingsSnap.empty);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    }
    fetchUserData();
  }, [userId]);

  // Fetch current user's data for notification sender name
  useEffect(() => {
    async function fetchCurrentUserData() {
      if (!currentUser) return;

      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setCurrentUserData(userSnap.data());
        }
      } catch (error) {
        console.error('Error fetching current user data:', error);
      }
    }
    fetchCurrentUserData();
  }, [currentUser]);

  // Helper function to create a notification
  const createNotification = async (type, recipientId, message, targetId = null) => {
    if (!currentUser || currentUser.uid === recipientId) return; // Don't notify self

    try {
      const notifRef = collection(db, `notifications/${recipientId}/userNotifications`);
      await addDoc(notifRef, {
        type,
        senderId: currentUser.uid,
        targetId,
        message,
        timestamp: serverTimestamp(),
        isRead: false
      });
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  // Handle follow/unfollow
  const handleFollow = async (e) => {
    e.stopPropagation();
    if (!currentUser) {
      navigate('/sign-in');
      return;
    }

    setIsFollowLoading(true);
    try {
      const wasFollowing = isFollowing(userId);
      await toggleFollow(userId);
      if (!wasFollowing) {
        // Notify only on follow, not unfollow
        const message = `started following you.`;
        await createNotification('follow', userId, message);
      }
    } catch (error) {
      console.error('Error handling follow:', error);
    } finally {
      setIsFollowLoading(false);
    }
  };

  // Navigate to user profile
  const handleProfileClick = (e) => {
    e.stopPropagation();
    navigate(`/profile/${userId}`);
  };

  // Determine follow button text and style
  const getFollowButtonState = () => {
    if (!isInitialized) {
      return {
        text: '...',
        className: 'bg-gray-500 text-white',
        icon: <FaUserPlus className="mr-1" />
      };
    }
    
    if (isFollowing(userId)) {
      return {
        text: 'Following',
        className: 'bg-black text-white hover:bg-gray-800',
        icon: <FaUserCheck className="mr-1" />
      };
    }
    if (shouldFollowBack(userId)) {
      return {
        text: 'Follow Back',
        className: 'bg-blue-500 text-white hover:bg-blue-600',
        icon: <FaUserPlus className="mr-1" />
      };
    }
    return {
      text: 'Follow',
      className: 'bg-white text-black hover:bg-gray-100',
      icon: <FaUserPlus className="mr-1" />
    };
  };

  const followState = getFollowButtonState();

  if (!isInitialized) {
    return (
      <div className="flex items-center py-4 px-4">
        <div className="w-12 h-12 rounded-full bg-gray-700 animate-pulse mr-3"></div>
        <div className="flex-1">
          <div className="h-4 w-24 bg-gray-700 rounded animate-pulse mb-1"></div>
          <div className="h-3 w-16 bg-gray-700 rounded animate-pulse"></div>
        </div>
        <div className="h-8 w-20 bg-gray-700 rounded-full animate-pulse"></div>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center py-4 px-4">
        {/* Profile Image */}
        <button
          onClick={handleProfileClick}
          className="w-12 h-12 rounded-full p-[2px] mr-3 focus:outline-none flex-shrink-0"
          aria-label={`View ${userData.name}'s profile`}
        >
          <img
            src={userData.profileImage}
            alt={`${userData.name}'s profile`}
            className="w-full h-full rounded-full object-cover border-2 border-gray-900"
            onError={(e) => (e.target.src = defaultAvatar)}
          />
        </button>

        {/* Name and Username */}
        <div className="flex-1 min-w-0" onClick={handleProfileClick}>
          <div className="flex items-center">
            <p className="font-bold text-white truncate max-w-[120px] sm:max-w-[180px]">
              {userData.name}
            </p>
            <VerificationBadge isVerified={userData.verified} />
          </div>
          <p className="text-gray-500 text-sm truncate max-w-[120px] sm:max-w-[180px]">@{userData.username}</p>
          
          {/* Seller Badge - Only show if user has listings */}
          {hasListings && (
            <div className="mt-1">
              <span 
                className="inline-block px-2 py-0.5 text-xs font-medium text-white rounded-full"
                style={{ backgroundColor: '#25D366' }}
              >
                Seller
              </span>
            </div>
          )}
        </div>

        {/* Follow Button */}
        {currentUser?.uid !== userId && (
          <button
            onClick={handleFollow}
            disabled={isFollowLoading}
            className={`px-4 py-1 rounded-full font-medium transition duration-300 focus:outline-none flex items-center ${
              followState.className
            } ${isFollowLoading ? 'opacity-70' : ''} flex-shrink-0`}
            aria-label={`${followState.text} ${userData.name}`}
          >
            {followState.icon}
            {isFollowLoading ? '...' : followState.text}
          </button>
        )}
      </div>
      <div className="border-t border-gray-700 mx-4"></div>
    </>
  );
}